import { useEffect, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import { dialogsEndpoints as endpoints } from '@/api/endpoints';
import DialogEditor from '@/components/organisms/Dialogs/DialogEditor';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { API } from '@/hooks/useDialogs';
import { popModal } from '@/redux/modals/actions';
import { restoreDialogState, setDialog } from '@/redux/nodes/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { RootState } from '@/shared/types/state';

interface Props {
  name: string;
  dialogId: string;
}

export const ModalPreviewDialog = ({ name, dialogId }: Props) => {
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore<RootState>();

  const editorStateRef = useRef(store.getState().nodes);
  const openedAtRef = useRef(pathname);
  const hasLeftPage = pathname !== openedAtRef.current;

  const { data: previewDialog, isError } = useQuery({
    queryKey: [endpoints.dialog(brainId, dialogId)],
    queryFn: () => API.getDialog(brainId, dialogId),
    enabled: !!brainId && !!dialogId,
  });

  const sliceDialogId = useSelector((state: RootState) => state.nodes.dialogId);

  useEffect(() => {
    if (hasLeftPage) {
      dispatch(popModal());
    }
  }, [dispatch, hasLeftPage]);

  useEffect(() => {
    if (!hasLeftPage && previewDialog && sliceDialogId !== previewDialog.dialog_id) {
      dispatch(setDialog(previewDialog));
    }
  }, [dispatch, hasLeftPage, previewDialog, sliceDialogId]);

  useEffect(
    () => () => {
      if (store.getState().nodes.dialogId === dialogId) {
        dispatch(restoreDialogState(editorStateRef.current));
      }
    },
    [dialogId, dispatch, store]
  );

  const isPreviewLoaded = sliceDialogId === dialogId;

  const closeModal = () => {
    dispatch(popModal());
  };

  return (
    <Dialog open onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="w-[90vw] max-w-[90vw] gap-6 p-6 sm:max-w-[90vw]">
        <DialogTitle className="text-xl leading-7 font-semibold text-foreground">
          {`${name} ${t('common.preview').toLowerCase()}`}
        </DialogTitle>

        <div className="min-h-[65vh] overflow-hidden rounded-lg border border-border">
          {isPreviewLoaded ? (
            <DialogEditor />
          ) : (
            <div className="flex min-h-[65vh] items-center justify-center">
              {isError ? (
                <p className="text-sm text-muted-foreground">{t('common.something_wrong')}</p>
              ) : (
                <Spinner size="lg" />
              )}
            </div>
          )}
        </div>

        <footer className="flex justify-end">
          <Button
            onClick={() => {
              navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${dialogId}`);
              dispatch(popModal());
            }}
            type="button"
            variant="secondary"
          >
            {t('ai_agents.reminder.go_to_dialog')}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
